import { createTheme } from '@mui/material/styles';

/**
 * 034 T006 — the MUI theme, bound to `tokens.scss` and to nothing else.
 *
 * **Every colour here is a `var()` naming a token; none is a value.** `tokens.scss` is the palette
 * and states it once, in both valuations — `:root` and `:root[data-theme='dark']`. Writing hexes
 * here would be a second definition that drifts, and a theme object rebuilt when the theme changes
 * would be a second mechanism deciding which palette is in force beside `ThemeController.apply()`.
 * Binding to the tokens avoids both: the dark swap is the stylesheet's, it happens in CSS, and this
 * object never changes. `cssVariables.nativeColor` (MUI 7.3) is what permits it — it derives
 * hover, selected and contrast shades with the browser's own colour functions instead of parsing a
 * value in JavaScript, which a `var()` cannot survive.
 *
 * **This was skipped at the port and the omission shipped.** Without a theme every MUI component
 * wore the library's defaults: the toolbar was MUI's blue, the drawer white in both palettes, and
 * every primary button, focused field and checkbox a hue that means nothing — 006 R-007's rule is
 * that colour means time pressure and nothing else. Angular's `mat.theme` had set `primary` to a
 * palette the tokens then overrode; here `primary` IS the ink, which is the same intent stated once.
 *
 * `mode` stays `light` in both palettes, deliberately. Mode switches MUI's own grey ramps and its
 * dark-paper elevation overlay, and every one of those is either restated below from the tokens or
 * unused. Letting it follow `data-theme` would need the object to be rebuilt, which is exactly the
 * second mechanism this file exists not to have.
 */

/** The ink at a given strength over whatever is beneath it — MUI's `action.*` shades, token-bound. */
const inkAt = (percent: number): string => `color-mix(in srgb, var(--ink) ${percent}%, transparent)`;

/** An opaque step from the ground toward the ink — MUI's grey ramp, re-derived per valuation. */
const inkOnPaper = (percent: number): string => `color-mix(in srgb, var(--ink) ${percent}%, var(--paper))`;

/**
 * MUI's neutrals, bound to the tokens as well — and the reason is `e2e/palette.spec.ts`, not taste.
 *
 * With `cssVariables` on, MUI declares its whole palette as custom properties on `:root`, and its
 * own neutrals — the grey ramp, `common.black`/`white` — arrive as HEXES. The palette guard counts
 * every hex token on `:root` and pins the count, so the first run of this theme put nineteen
 * library greys beside the twelve tokens and turned it red. They were all neutrals, so no rule was
 * broken; but a guard that counts tokens must not be made to count a library's, and moving MUI's
 * declarations to another selector to get them out of its sight would be the blind guard this
 * repository keeps recording. Binding them is also simply more correct: a fixed `grey[300]` stays
 * light on the dark ground, while a step from paper toward ink inverts with it.
 *
 * The ramp's percentages follow the lightness of MUI's own greys — `grey[50]` is almost ground,
 * `grey[900]` almost ink — so a component choosing a step lands at the same relative weight.
 */
const GREY = {
  50: inkOnPaper(3), 100: inkOnPaper(6), 200: inkOnPaper(11), 300: inkOnPaper(18), 400: inkOnPaper(32),
  500: inkOnPaper(45), 600: inkOnPaper(58), 700: inkOnPaper(70), 800: inkOnPaper(82), 900: inkOnPaper(90),
  A100: inkOnPaper(6), A200: inkOnPaper(11), A400: inkOnPaper(32), A700: inkOnPaper(70),
};

/**
 * The three the public types do not declare. `createThemeWithVars` fills each with a hex ONLY when
 * the key is unset (`setColor`), so stating them is what keeps them off the palette guard's `:root`
 * count; the types lag the runtime here, which is why they travel in a spread rather than inline.
 */
const UNTYPED_NEUTRALS = {
  common: { black: 'var(--ink)', white: 'var(--paper-raised)', background: 'var(--paper)', onBackground: 'var(--ink)' },
  SnackbarContent: { bg: 'var(--ink)', color: 'var(--paper)' },
} as object;

export const theme = createTheme({
  cssVariables: { nativeColor: true },
  palette: {
    mode: 'light',
    ...UNTYPED_NEUTRALS,
    grey: GREY,
    primary: { main: 'var(--ink)', contrastText: 'var(--paper)' },
    secondary: { main: 'var(--ink)', contrastText: 'var(--paper)' },
    // The one state that needs a colour in a form is a refusal, and the palette's only "something is
    // wrong" colour is the overdue band. Reusing it spends no new hue; warning, info and success are
    // not used by any component here and stay neutral so that adopting one cannot introduce a hue.
    error: { main: 'var(--overdue)', contrastText: 'var(--paper)' },
    warning: { main: 'var(--ink)', contrastText: 'var(--paper)' },
    info: { main: 'var(--ink)', contrastText: 'var(--paper)' },
    success: { main: 'var(--ink)', contrastText: 'var(--paper)' },
    text: { primary: 'var(--ink)', secondary: 'var(--muted)', disabled: inkAt(38) },
    divider: 'var(--rule)',
    background: { default: 'var(--paper)', paper: 'var(--paper-raised)' },
    action: {
      active: 'var(--muted)',
      hover: inkAt(6),
      selected: inkAt(10),
      focus: inkAt(12),
      disabled: inkAt(38),
      disabledBackground: inkAt(12),
    },
  },
  typography: { fontFamily: 'var(--font-body)' },
  components: {
    /**
     * The toolbar is ground, not a colour bar. Angular Material 3's `mat-toolbar` painted the theme's
     * neutral surface; MUI's AppBar defaults to `primary`, filled and shadowed. No rule under it
     * either — the maintainer removed the hairline 034 first gave it (2026-09-24).
     */
    MuiAppBar: {
      defaultProps: { color: 'inherit', elevation: 0 },
      styleOverrides: {
        root: { backgroundColor: 'var(--paper)', color: 'var(--ink)' },
      },
    },
    /**
     * The navigation is ground too — `mat-sidenav` was surface, where MUI's paper is raised white.
     * And no rule beside it: a docked MUI drawer draws a `divider` border on its content edge, which
     * the maintainer removed (2026-09-24). `paperAnchorDockedLeft` is the slot that draws it.
     */
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: 'var(--paper)', color: 'var(--ink)' },
        paperAnchorDockedLeft: { borderRight: 'none' },
      },
    },
  },
});
